import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Trash2 } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import MoneyInput from "@/components/MoneyInput";

export const AjusteSaldoDialog = ({ open, onOpenChange, balance, onSaved }) => {
  const [real, setReal] = useState(0);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setReal(0); setReason(""); } }, [open]);
  const diff = Math.round((Number(real || 0) - Number(balance || 0)) * 100) / 100;
  const submit = async () => {
    if (!reason.trim()) return toast.error("Informe o motivo do ajuste");
    setSaving(true);
    try { await api.post("/finance/caixa/ajustes", { real_balance: Number(real || 0), reason }); toast.success("Saldo ajustado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao ajustar"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] sm:w-full" data-testid="ajuste-dialog">
        <DialogHeader><DialogTitle className="font-heading font-black text-sky-800">Ajuste de saldo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-slate-50 p-3 flex justify-between text-sm"><span className="text-slate-500">Saldo no sistema</span><b className="tabular-nums" data-testid="ajuste-current">{BRL(balance)}</b></div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Saldo real no banco</label>
            <MoneyInput value={real} onChange={setReal} className="w-full" data-testid="ajuste-real" />
          </div>
          {Number(real) > 0 && (
            <div className={`rounded-xl p-3 flex justify-between text-sm font-bold ${diff >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="ajuste-diff">
              <span>Ajuste</span><span className="tabular-nums">{diff >= 0 ? "+ " : "− "}{BRL(Math.abs(diff))}</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Motivo</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Ex.: conferência com extrato do banco" data-testid="ajuste-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || diff === 0} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="ajuste-submit">{saving ? "Salvando..." : "Ajustar saldo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AjustesHistory = ({ items, onChanged }) => {
  if (!items?.length) return null;
  const remove = async (a) => {
    if (!window.confirm("Desfazer este ajuste? O saldo volta ao valor anterior a ele.")) return;
    try { await api.delete(`/finance/caixa/ajustes/${a.id}`); toast.success("Ajuste desfeito"); onChanged(); }
    catch { toast.error("Erro ao desfazer"); }
  };
  return (
    <section className="bg-white rounded-2xl border border-sky-200 p-5 card-shadow" data-testid="caixa-ajustes-section">
      <h2 className="font-heading text-base font-black text-sky-900 flex items-center gap-2 mb-3"><Scale className="w-4 h-4" /> Ajustes de saldo</h2>
      <ul className="divide-y divide-slate-100">
        {items.map((a) => (
          <li key={a.id} className="py-2.5 flex items-start gap-3" data-testid={`caixa-ajuste-${a.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-black text-slate-900 leading-tight break-words">{a.reason}</p>
              <p className="text-[10.5px] text-slate-500">{formatDateBR(a.date)} • {a.created_by} • {BRL(a.previous_balance)} → {BRL(a.new_balance)}</p>
            </div>
            <p className={`text-[13px] font-black tabular-nums shrink-0 ${a.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{a.amount >= 0 ? "+ " : "− "}{BRL(Math.abs(a.amount))}</p>
            <button onClick={() => remove(a)} className="text-slate-400 hover:text-rose-600" title="Desfazer" data-testid={`caixa-ajuste-del-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
    </section>
  );
};
